import { atom } from 'jotai'
import { atomWithStorage, createJSONStorage } from 'jotai/utils'
import {
  initialWorkOrderIdAtom,
  emailLinkWorkOrderIdAtom,
} from '../authentication/login'
import type {
  DashboardInfoCoreTrackResponse,
  DashboardInfoResponseLoadable,
  PackageDocumentDetailsLoadable,
  ReadOnlyRequest,
  WorkItem,
} from '@/types/dashboard'
import { getDashboardInfo } from '@/services/dashboard/getDashboardInfo'
import { getReadOnlyInfo } from '@/services/dashboard/getReadOnlyInfo'

const _selectedWorkOrderIdBaseAtom = atom<number | null>(null)

export const selectedWorkOrderIdAtom = atom<
  number | null,
  [number | null],
  void
>(
  (get) => {
    // Read only mode only uses this tab's own work order.
    const readOnlyRequest = get(readOnlyRequestAtom)
    if (readOnlyRequest !== null) {
      // old link: the work order id is part of the request. new link: it comes with the dashboard info
      return 'workOrderId' in readOnlyRequest
        ? readOnlyRequest.workOrderId
        : (get(dashboardInfoAtom).data?.data?.dashBoardInfo?.[0]?.workOrderId ??
            null)
    }

    const selected = get(_selectedWorkOrderIdBaseAtom)
    // If user has already selected something, use it
    if (selected !== null) return selected
    // Email-link WO is the highest-priority source for the initial context
    const emailLinkWO = get(emailLinkWorkOrderIdAtom)
    if (emailLinkWO !== null && emailLinkWO > 0) return emailLinkWO
    return get(initialWorkOrderIdAtom)
  },
  (_, set, newValue) => {
    set(_selectedWorkOrderIdBaseAtom, newValue)
  },
)

export const dashboardInfoAtom = atom<DashboardInfoResponseLoadable>({
  isLoading: false,
  data: null,
})

// Session-scoped so CoreTrack read-only mode survives a page refresh in the same tab.
export const readOnlyRequestAtom = atomWithStorage<ReadOnlyRequest | null>(
  'readOnlyRequest',
  null,
  createJSONStorage<ReadOnlyRequest | null>(() => sessionStorage),
  { getOnInit: true },
)

export const fetchDashboardInfoAtom = atom(null, async (get, set) => {
  set(dashboardInfoAtom, { isLoading: true, data: null })
  set(packageDocumentDetailsAtom, { isLoading: true, data: null })
  const readOnlyRequest = get(readOnlyRequestAtom)

  // If we have a readOnlyRequest, it means a coretrack user navigated
  //  from coretrack for readonly mode, so we should call the getReadOnlyInfo endpoint instead of the regular getDashboardInfo endpoint.
  //  For new links the BFF decrypts the url, so the work order id comes back in the response.
  if (readOnlyRequest !== null) {
    try {
      const result = await getReadOnlyInfo(readOnlyRequest)

      if (!result.isSuccess || result.data === null) {
        if (!result.needsToLogin) {
          const { redirectToLogin } = await import('@/services/redirectToLogin')
          await redirectToLogin()
        }
        return
      }

      set(dashboardInfoAtom, { isLoading: false, data: result })
      set(packageDocumentDetailsAtom, {
        isLoading: false,
        data: {
          data: result.data?.packageDocumentDetails ?? null,
          registrationStatus: result.registrationStatus,
          isSuccess: result.isSuccess,
          needsToLogin: result.needsToLogin,
        },
      })
    } catch (_error) {
      set(dashboardInfoAtom, { isLoading: false, data: null })
      set(packageDocumentDetailsAtom, { isLoading: false, data: null })
    }
    return
  }

  const workOrderId = get(selectedWorkOrderIdAtom)
  if (!workOrderId) {
    set(dashboardInfoAtom, { isLoading: false, data: null })
    set(packageDocumentDetailsAtom, { isLoading: false, data: null })
    return
  }
  try {
    const result = await getDashboardInfo(workOrderId)
    set(dashboardInfoAtom, { isLoading: false, data: result })
    set(packageDocumentDetailsAtom, {
      isLoading: false,
      data: {
        data: result.data?.packageDocumentDetails ?? null,
        registrationStatus: result.registrationStatus,
        isSuccess: result.isSuccess,
        needsToLogin: result.needsToLogin,
      },
    })
  } catch (_error) {
    // On error, ensure loading is reset and data is cleared
    set(dashboardInfoAtom, { isLoading: false, data: null })
    set(packageDocumentDetailsAtom, { isLoading: false, data: null })
  }
})

export const selectedDashboardInfoAtom =
  atom<DashboardInfoCoreTrackResponse | null>((get) => {
    const workOrderId = get(selectedWorkOrderIdAtom)
    const dashboardInfo = get(dashboardInfoAtom)
    if (!dashboardInfo || !workOrderId) return null
    return (
      dashboardInfo.data?.data?.dashBoardInfo?.find(
        (item: DashboardInfoCoreTrackResponse) =>
          item.workOrderId === workOrderId,
      ) ?? null
    )
  })

export const selectedWorkOrderAtom = atom<WorkItem | null>((get) => {
  const workOrderId = get(selectedWorkOrderIdAtom)
  const dashboardInfo = get(dashboardInfoAtom)
  if (!dashboardInfo || !workOrderId) return null
  return (
    dashboardInfo.data?.data?.workItems?.multipleWorkorders.find(
      (item: WorkItem) => item.workOrderId === workOrderId,
    ) ?? null
  )
})

export const packageDocumentDetailsAtom = atom<PackageDocumentDetailsLoadable>({
  isLoading: false,
  data: null,
})
